/* global gtag */
import React from "react";
import PropTypes from "prop-types";
import { COMPANY_REVIEW_DATA } from "@/utils/const";
import { gtag_report_conversion } from "@/components/analytics/conversion";

/**
 * ExternalLinkWrapper - Wraps components with affiliate/external links
 *
 * Features:
 * - Extracts affiliate links from COMPANY_REVIEW_DATA
 * - Tracks Google Analytics events with event_callback to ensure completion
 * - Uses 2-second fallback timeout if callback doesn't fire
 * - Navigates only after analytics completes or times out
 *
 * Props:
 * @param {string} sourceId - Required identifier for the link source
 * @param {string} company - Company name to match in COMPANY_REVIEW_DATA
 * @param {string} name - Alternative way to identify company by name
 * @param {string} webDomain - Alternative way to identify company by domain
 * @param {string} customUrl - Optional custom URL to use instead of affiliate link
 * @param {React.ReactNode} children - Component(s) to wrap
 * @param {string} className - Additional CSS classes
 */
const ExternalLinkWrapper = ({
  sourceId,
  company,
  name,
  webDomain,
  customUrl,
  children,
  className = "",
  ...otherProps
}) => {
  // Extract affiliate link from COMPANY_REVIEW_DATA
  const getAffiliateLink = () => {
    // If custom URL is provided, use it
    if (customUrl) {
      return customUrl;
    }

    // Find matching company data by name, webDomain, or company prop
    const companyData = COMPANY_REVIEW_DATA.find(
      (item) =>
        (name && item.name === name) ||
        (webDomain && item.webDomain === webDomain) ||
        (company && item.name === company),
    );

    return companyData?.affiliateLink || "#";
  };

  const affiliateLink = getAffiliateLink();

  // Handle click with analytics tracking and callback-based navigation
  const handleClick = async (e) => {
    e.preventDefault();

    // Skip if no sourceId provided
    if (!sourceId) {
      console.warn("ExternalLinkWrapper: sourceId is required");
      return;
    }

    // Navigate to the link
    const navigateToLink = () => {
      if (affiliateLink && affiliateLink !== "#") {
        window.location.href = affiliateLink;
      }
    };

    // Fire Google Ads conversion tracking and wait for it to complete
    await gtag_report_conversion();

    // Track analytics event with callback
    try {
      if (
        typeof gtag !== "undefined" &&
        process.env.NODE_ENV !== "development"
      ) {
        // Use event_callback to ensure analytics completes before navigation
        gtag("event", "affiliate_link_click", {
          source_id: sourceId,
          company: company || name,
          web_domain: webDomain,
          link_url: affiliateLink,
          event_category: "affiliate",
          event_label: `${sourceId}_${company || name || webDomain}`,
          event_callback: navigateToLink,
          event_timeout: 2000, // Fallback timeout if callback doesn't fire
        });
      } else if (process.env.NODE_ENV === "development") {
        console.log("Analytics Event (dev mode):", {
          event: "affiliate_link_click",
          source_id: sourceId,
          company: company || name,
          web_domain: webDomain,
          link_url: affiliateLink,
        });
        // In dev mode, navigate after short delay
        setTimeout(navigateToLink, 500);
      } else {
        // No analytics available, navigate immediately
        navigateToLink();
      }
    } catch (error) {
      console.error("Error tracking analytics event:", error);
      // Navigate anyway if analytics fails
      navigateToLink();
    }
  };

  return (
    <a
      href={affiliateLink}
      onClick={handleClick}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
      {...otherProps}
    >
      {children}
    </a>
  );
};

ExternalLinkWrapper.propTypes = {
  sourceId: PropTypes.string.isRequired,
  company: PropTypes.string,
  name: PropTypes.string,
  webDomain: PropTypes.string,
  customUrl: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default ExternalLinkWrapper;
